import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { doc, getDoc, updateDoc, Timestamp, collection, query, where, getDocs } from "firebase/firestore";

import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Drawer,
  Image,
  Loader,
  MediaQuery,
  Popover,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";

import { db } from "../../firebase";
import { DataEntry } from "../shared";
import { fetchAndDownloadReportCombined } from "../shared/hooks/generate-report";

import { isNumber, formatValue, toValidDate } from "./hooks";
import { deleteTemplate, updateTemplate } from "../shared/hooks/data-functions";
import { ListingConfig } from "./configurations";
import {
  Check,
  InfoCircle
} from "tabler-icons-react";
import DownloadAlert from "../shared/downloadAlert";
import VerifiedIcon from "./verified-icon"
import { ResponsiveDataTable } from "./responsive-data-table";

export interface DataEntryTableProps {
  elements: Array<Record<string, any> & { id: string }>;
  config: ListingConfig;
  setDocuments: React.Dispatch<React.SetStateAction<any[]>>;
  templateName: string;
  companyId: string;
  selectedEntryId: string | null | undefined;
  entryMode: "view" | "edit" | null | undefined;
  onOpenEntry: (entryId: string, mode: "view" | "edit", options?: { replace?: boolean }) => void;
  onCloseEntry: (options?: { replace?: boolean }) => void;
  onResolvedEntry?: (entry: any, options: { skipAutoAlign: boolean }) => void;
}

export function DataEntryTable({
  elements,
  config,
  setDocuments,
  templateName,
  companyId,
  selectedEntryId,
  entryMode,
  onOpenEntry,
  onCloseEntry,
  onResolvedEntry,
}: DataEntryTableProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [opened, setOpened] = useState<boolean>(false);
  const [focus, setFocused] = useState<Record<string, any> | undefined>();
  const [doEdit, setDoEdits] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [notification, setNotification] = useState<{ success: boolean; message: string } | undefined>();
  const [renderType, setRendertType] = useState<string | undefined>();
  const [reportName, setReportName] = useState<string | undefined>();
  const [Base64File, setBase64File] = useState<string | undefined>();
  const [downloadModalOpen, setDownloadModalOpen] = useState<boolean>(false);
  const [selectOptions, setSelectOptions] = useState<Record<string, any[]>>({});
  const [moreOpenedDesktop, setMoreOpenedDesktop] = useState<boolean>(false);
  const [moreOpenedMobile, setMoreOpenedMobile] = useState<boolean>(false);
  const [syncFleetOdometer, setSyncFleetOdometer] = useState<boolean>(false);
  const [userToggledSyncOdo, setUserToggledSyncOdo] = useState<boolean>(false);

  let tableSortedKeys: string[];
  let detailsSortedKeys: string[];
  const initialValues: Record<string, any> = {};
  const validate: Record<string, (value: any) => string | null> = {};

  const fieldConfig = config.fieldConfig;
  const reportConfig = config.reportConfig;

  useEffect(() => {
    const fetchSelectOptions = async () => {
      const collectionsToFetch = new Set<string>();
      Object.keys(fieldConfig || {}).forEach(key => {
        if (fieldConfig[key].type === "SELECT" && fieldConfig[key].sourceCollection) {
          collectionsToFetch.add(fieldConfig[key].sourceCollection!);
        }
      });

      const options: Record<string, any[]> = {};
      const collectionsToFetchArray = Array.from(collectionsToFetch);
      for (const collectionName of collectionsToFetchArray) {
        try {
          if (collectionName === "sections") {
            // Sections are nested in zones. We fetch zones to get the sections.
            const q = query(collection(db, "areaForFleetUse"), where("companyId", "==", companyId));
            const snapshot = await getDocs(q);
            const zones = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as any);
            options["sections"] = zones.flatMap(z => (z.sections || []).map((s: any) => ({
              id: s.id,
              ...s,
              sectionName: s.sectionName,
              // We use sectionName as the label field if sourceFieldForLabel is not specified
            })));
          } else {
            const q = query(collection(db, collectionName), where("companyId", "==", companyId));
            const snapshot = await getDocs(q);
            options[collectionName] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          }
        } catch (error) {
          console.error("Failed to fetch options for", collectionName, error);
        }
      }
      setSelectOptions(options);
    };

    if (companyId) {
      fetchSelectOptions();
    }
  }, [companyId, fieldConfig]);

  let editableKeys = useMemo(
    () => Object.keys(fieldConfig).filter((c) => fieldConfig[c].editable),
    [fieldConfig]
  );

  tableSortedKeys = Object.keys(fieldConfig)
    .filter((c) => fieldConfig[c].tableRank !== undefined && fieldConfig[c].tableRank! >= 0)
    .sort((a, b) => (fieldConfig[a].tableRank ?? 0) - (fieldConfig[b].tableRank ?? 0));

  detailsSortedKeys = Object.keys(fieldConfig)
    .filter((c) => fieldConfig[c].detailsRank !== undefined && fieldConfig[c].detailsRank! >= 0)
    .sort((a, b) => (fieldConfig[a].detailsRank ?? 0) - (fieldConfig[b].detailsRank ?? 0));

  editableKeys.forEach((v) => (initialValues[v] = undefined));

  editableKeys
    .filter((c) => fieldConfig[c].type === "NUMBER")
    .forEach((v) => (validate[v] = (value) => isNumber(value)));

  const form = useForm({
    initialValues,
    validate,
  });

  useEffect(() => {
    if (focus) {
      editableKeys.forEach((k) => {
        form.setFieldValue(k, formatValue(fieldConfig[k].type, focus[k]));
      });
    }
    setSyncFleetOdometer(false);
    setUserToggledSyncOdo(false);
  }, [focus]);

  const activeFleetId = (form.values.fleetId ?? focus?.fleetId) as string | undefined;
  const activeVehicle = useMemo(
    () => (selectOptions["fleet"] || []).find((v: any) => v.id === activeFleetId),
    [selectOptions, activeFleetId]
  );

  const rawFormOdo =
    form.values.odoOrHourReading !== undefined
      ? form.values.odoOrHourReading
      : focus?.odoOrHourReading;
  const parsedFormOdo =
    rawFormOdo !== undefined && rawFormOdo !== null && rawFormOdo !== ""
      ? Number(String(rawFormOdo).replace(",", "."))
      : NaN;
  const parsedFocusOdo =
    focus?.odoOrHourReading !== undefined &&
    focus?.odoOrHourReading !== null &&
    focus?.odoOrHourReading !== ""
      ? Number(String(focus.odoOrHourReading).replace(",", "."))
      : NaN;
  const parsedVehicleOdo =
    activeVehicle?.lastOdometer !== undefined &&
    activeVehicle?.lastOdometer !== null &&
    activeVehicle?.lastOdometer !== ""
      ? Number(activeVehicle.lastOdometer)
      : NaN;
  const isOdoModifiedInForm =
    Number.isFinite(parsedFormOdo) &&
    (!Number.isFinite(parsedFocusOdo) || parsedFormOdo !== parsedFocusOdo);

  useEffect(() => {
    if (!doEdit) {
      setSyncFleetOdometer(false);
      setUserToggledSyncOdo(false);
      return;
    }
    if (
      !userToggledSyncOdo &&
      isOdoModifiedInForm &&
      Number.isFinite(parsedVehicleOdo) &&
      Number.isFinite(parsedFocusOdo) &&
      parsedVehicleOdo === parsedFocusOdo
    ) {
      setSyncFleetOdometer(true);
    }
  }, [
    doEdit,
    isOdoModifiedInForm,
    parsedFocusOdo,
    parsedVehicleOdo,
    userToggledSyncOdo,
  ]);

  useEffect(() => {
    if (!selectedEntryId) {
      setOpened(false);
      setFocused(undefined);
      setDoEdits(false);
      setMoreOpenedDesktop(false);
      setMoreOpenedMobile(false);
      return;
    }

    const loadedElement = elements.find((element) => element.id === selectedEntryId);
    if (loadedElement) {
      setFocused(loadedElement);
      setOpened(true);
      setMoreOpenedDesktop(false);
      setMoreOpenedMobile(false);
      onResolvedEntry?.(loadedElement, { skipAutoAlign: true });
      return;
    }

    let cancelled = false;

    const loadEntry = async () => {
      try {
        const snapshot = await getDoc(doc(db, templateName, selectedEntryId));
        if (!snapshot.exists() || cancelled) {
          if (!cancelled) {
            onCloseEntry?.({ replace: true });
          }
          return;
        }

        const loadedEntry = { id: snapshot.id, ...snapshot.data() };
        setFocused(loadedEntry);
        setOpened(true);
        onResolvedEntry?.(loadedEntry, { skipAutoAlign: false });
      } catch (error) {
        console.error("Failed to load data entry", error);
        if (!cancelled) {
          onCloseEntry?.({ replace: true });
        }
      }
    };

    loadEntry();

    return () => {
      cancelled = true;
    };
  }, [elements, onCloseEntry, onResolvedEntry, selectedEntryId, templateName]);

  useEffect(() => {
    if (!selectedEntryId) {
      return;
    }

    setDoEdits(entryMode === "edit");
  }, [entryMode, selectedEntryId]);

  const handleTransactionReviewed = async () => {
    if (!focus) return;
    setLoading(true);
    const updateResp = await updateTemplate(templateName, focus.id, companyId, {
      transactionReviewed: true,
    });
    if (updateResp.code === 200) {
      setNotification({
        success: true,
        message: "Entry marked as reviewed",
      });
      const updatedValue = { ...focus, transactionReviewed: true };

      setDocuments((docs) => {
        const newDocs = [...docs];
        const index = newDocs.findIndex((d) => d.id === focus.id);
        if (index !== -1) {
          newDocs[index] = updatedValue;
        }
        return newDocs;
      });
    } else {
      setNotification({
        success: false,
        message: "Entry review failed",
      });
    }
    setTimeout(() => {
      setNotification(undefined);
      setLoading(false);
      onCloseEntry?.({ replace: false });
    }, 2000);
  };

  const handleTransactionDeleted = async () => {
    if (!focus) return;
    setLoading(true);
    const updateResp = await deleteTemplate(templateName, focus.id, companyId);
    setLoading(false);
    if (updateResp.code === 200) {
      setNotification({
        success: true,
        message: "Entry deleted",
      });
      setDocuments((docs) => {
        const newDocs = [...docs];
        const index = newDocs.findIndex((d) => d.id === focus.id);
        if (index !== -1) {
          newDocs.splice(index, 1);
        }
        return newDocs;
      });
      onCloseEntry?.({ replace: false });
    } else {
      setNotification({
        success: false,
        message: "Entry deletion failed",
      });
    }
    setTimeout(() => {
      setNotification(undefined);
    }, 2000);
  };

  const updateEntry = useCallback(
    async (updatedValues: Record<string, any>, previousValues: Record<string, any>) => {
      if (!focus) return;
      const handleError = (error: unknown) => {
        setNotification({
          success: false,
          message: "Entry could not be updated.",
        });
        setLoading(false);
        onOpenEntry?.(focus?.id, "view", { replace: true });
        setTimeout(() => {
          setNotification(undefined);
        }, 3000)
        console.error(error);
      };
      const originalValues = { ...previousValues };
      const targetFleetId = (updatedValues.fleetId ?? originalValues.fleetId) as string | undefined;
      const rawOdoValue =
        updatedValues.odoOrHourReading !== undefined
          ? updatedValues.odoOrHourReading
          : originalValues.odoOrHourReading;
      const parsedOdoForFleet =
        rawOdoValue !== undefined && rawOdoValue !== null && rawOdoValue !== ""
          ? parseFloat(String(rawOdoValue).replace(",", "."))
          : NaN;
      const shouldSyncFleetOdo = Boolean(
        syncFleetOdometer &&
          targetFleetId &&
          Number.isFinite(parsedOdoForFleet) &&
          parsedOdoForFleet >= 0
      );

      try {
        for (const key of Object.keys(updatedValues)) {
          if (!fieldConfig[key]) {
            // Security: If the key is not defined in the configuration, do not allow updating it.
            delete updatedValues[key];
            continue;
          }

          // Since we can only set minute/hour/day/month - we must only evaluate if those had changed
          if (fieldConfig[key]?.type === "DATE" && updatedValues[key] && originalValues[key]) {
            const originalDate = toValidDate(originalValues[key]);
            if (!originalDate) {
              updatedValues[key] = new Date(updatedValues[key]).toISOString();
            } else {
              const newDate = new Date(updatedValues[key]);

              const originalToDateString = originalDate.toDateString();
              const originalHours = originalDate.getHours();
              const originalMinutes = originalDate.getMinutes();
              const updatedToDateString = newDate.toDateString();
              const updatedHours = newDate.getHours();
              const updatedMinutes = newDate.getMinutes();

              if (originalToDateString !== updatedToDateString ||
                originalHours !== updatedHours ||
                originalMinutes !== updatedMinutes) {
                // If the date has changed, convert it to a Timestamp
                updatedValues[key] = newDate.toISOString();
              } else {
                // If the date hasn't changed, delete it from the update object
                delete updatedValues[key];
              }
            }
          } else if (updatedValues[key] === originalValues[key]) {
            // If the value hasn't changed, delete it from the update object
            delete updatedValues[key];
          } else if (fieldConfig[key]?.type === "NUMBER") {
            if (updatedValues[key] === "" || updatedValues[key] == null) {
              updatedValues[key] = null;
            } else if (typeof updatedValues[key] === "string") {
              updatedValues[key] = parseFloat(updatedValues[key].replace(',', '.'));
            }
          } else if (updatedValues[key] === "" || updatedValues[key] === undefined) {
            // Consistency: If the value is empty or undefined, set it to null so Firestore creates the key
            updatedValues[key] = null;
          }
        }
      } catch (error) {
        handleError(error);
      }
      if (Object.keys(updatedValues).length === 0 && !shouldSyncFleetOdo) {
        setLoading(false);
        onOpenEntry?.(focus.id, "view", { replace: true });
        return;
      }
      try {
        setLoading(true);
        const payloadForApi = shouldSyncFleetOdo
          ? { ...updatedValues, syncFleetOdometer: true }
          : updatedValues;
        const updateResp = await updateTemplate(
          templateName,
          focus.id,
          companyId,
          payloadForApi
        );
        setLoading(false);

        if (updateResp.code === 200) {
          if (shouldSyncFleetOdo && targetFleetId) {
            if (!updateResp.fleetOdometerUpdated) {
              try {
                await updateDoc(doc(db, "fleet", targetFleetId), {
                  lastOdometer: parsedOdoForFleet,
                  lastOdometerDate: new Date().toISOString(),
                });
              } catch (err) {
                console.warn("Fleet odometer updated via API; direct Firestore sync skipped:", err);
              }
            }
            setSelectOptions((prev) => ({
              ...prev,
              fleet: (prev.fleet || []).map((v: any) =>
                v.id === targetFleetId ? { ...v, lastOdometer: parsedOdoForFleet } : v
              ),
            }));
          }

          const formattedResponse: Record<string, any> = {};
          if (updateResp.data) {
            Object.keys(updateResp.data)
              .filter((k) => editableKeys.includes(k))
              .forEach(
                (k) =>
                (formattedResponse[k] = formatValue(
                  fieldConfig[k].type,
                  updateResp.data[k]
                ))
              );
          }
          const updatedValue = { ...focus, ...updatedValues, ...formattedResponse };

          setDocuments((docs) => {
            const newDocs = [...docs];
            const index = newDocs.findIndex((d) => d.id === focus.id);
            if (index !== -1) {
              newDocs[index] = updatedValue;
            }
            return newDocs;
          });
          setFocused(updatedValue);
          onOpenEntry?.(focus.id, "view", { replace: true });
          setNotification({
            success: true,
            message: shouldSyncFleetOdo
              ? "Entry and vehicle odometer successfully updated."
              : "Entry successfully updated.",
          });
        } else {
          handleError("Unknown update failure");
        }
      } catch (error) {
        handleError(error);
      }
      setTimeout(() => {
        setNotification(undefined);
      }, 2000);
    },
    [companyId, editableKeys, fieldConfig, focus, onOpenEntry, setDocuments, syncFleetOdometer, templateName]
  );

  const fetchReportConfig = async () => {
    const documentSnapshot = await getDoc(
      doc(db, "reportSetUp", reportConfig.reportId)
    );

    if (!documentSnapshot.exists()) {
      throw new Error("report config doesn't exist or is inactive.");
    }

    const data = documentSnapshot.data();
    if (!data) {
      throw new Error("report config has no data");
    }
    return data;
  };

  const handleGenerateReport = async (reportId: string, documentId: string) => {
    const reportToGenerate = await fetchReportConfig();

    const reportRenderType = reportToGenerate.renderingType;
    const reportName =
      reportToGenerate.reportShortName +
      `-${new Date().toISOString().split("T")[0]}`;

    try {
      const result = await fetchAndDownloadReportCombined(
        reportId,
        null,
        null,
        reportRenderType,
        reportName,
        documentId
      );

      setRendertType(reportRenderType);
      setReportName(reportName);
      setBase64File(result);
      setDownloadModalOpen(true);
    } catch (error) {
      alert("Your report could not be generated. An error notification has been sent to the CaptivaData team for further investigation.");
    }

    setLoading(false);
  };

  const renderButtons = (type: "desktop" | "mobile") => {
    if (!focus) return null;
    const isMobile = type === "mobile";
    const opened = isMobile ? moreOpenedMobile : moreOpenedDesktop;
    const setOpened = isMobile ? setMoreOpenedMobile : setMoreOpenedDesktop;

    return (
      <Fragment>
        {doEdit && (
          <Button size="md" color="cap-navy" fullWidth type="submit">
            Save
          </Button>
        )}
        {!doEdit && (
          <>
            <Button
              color="cap-blue"
              fullWidth
              variant="outline"
              size="md"
              type="button"
              onClick={() => {
                onOpenEntry?.(focus.id, "edit", { replace: false });
              }}
            >
              Edit
            </Button>
            <Popover
              opened={opened}
              onClose={() => setOpened(false)}
              target={
                <Button
                  color="cap-navy"
                  variant="outline"
                  fullWidth
                  size="md"
                  type="button"
                  onClick={() => setOpened((o) => !o)}
                >
                  More actions
                </Button>
              }
              width={260}
              position="bottom"
              withArrow
            >
              <div style={{ display: "grid", gap: "12px" }}>
                {!/iPhone|iPod/.test(navigator.userAgent) && (
                  <>
                    <Button
                      color="cap-blue"
                      variant="outline"
                      size="md"
                      fullWidth
                      type="button"
                      disabled={!config?.reportConfig?.reportId}
                      onClick={() => {
                        handleGenerateReport(
                          config.reportConfig.reportId,
                          focus.id
                        );
                        setLoading(true);
                      }}
                    >
                      Generate report
                    </Button>
                  </>
                )}
                {focus["transactionReviewed"] !== undefined && (
                  <Button
                    color="cap-green"
                    size="md"
                    fullWidth
                    type="button"
                    disabled={focus["transactionReviewed"]}
                    onClick={handleTransactionReviewed}
                  >
                    Mark as reviewed
                  </Button>
                )}
                <Button
                  color="red"
                  variant="outline"
                  fullWidth
                  size="md"
                  type="button"
                  onClick={handleTransactionDeleted}
                >
                  Delete
                </Button>
              </div>
            </Popover>
          </>
        )}
      </Fragment>
    );
  };

  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const handleSort = (colKey: string) => {
    if (sortColumn === colKey) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn(null);
        setSortDirection("asc");
      }
    } else {
      setSortColumn(colKey);
      setSortDirection("asc");
    }
  };

  const sortedElements = useMemo(() => {
    if (!sortColumn) return elements;

    const sorted = [...elements];
    const modifier = sortDirection === "asc" ? 1 : -1;
    const colType = fieldConfig[sortColumn]?.type;

    sorted.sort((a: any, b: any) => {
      const valA = a[sortColumn];
      const valB = b[sortColumn];

      const isNullA = valA === undefined || valA === null || valA === "";
      const isNullB = valB === undefined || valB === null || valB === "";
      if (isNullA && isNullB) return 0;
      if (isNullA) return 1;
      if (isNullB) return -1;

      if (colType === "date" || colType === "datetime" || sortColumn === "transactionDate") {
        const getDateMs = (ts: any) => {
          if (!ts) return 0;
          if (typeof ts.toMillis === "function") return ts.toMillis();
          if (typeof ts.toDate === "function") return ts.toDate().getTime();
          if (ts instanceof Date) return ts.getTime();
          if (typeof ts === "number") return ts;
          return new Date(ts).getTime() || 0;
        };
        return (getDateMs(valA) - getDateMs(valB)) * modifier;
      }

      const numA = Number(valA);
      const numB = Number(valB);
      if (!isNaN(numA) && !isNaN(numB) && typeof valA !== "boolean" && typeof valB !== "boolean") {
        return (numA - numB) * modifier;
      }

      const strA = String(valA);
      const strB = String(valB);
      return strA.localeCompare(strB, undefined, { numeric: true, sensitivity: "base" }) * modifier;
    });

    return sorted;
  }, [elements, sortColumn, sortDirection, fieldConfig]);

  const tableColumns = tableSortedKeys.map((key, index) => ({
    key,
    label: fieldConfig[key]?.label,
    render: (element: any) => <>{formatValue(fieldConfig[key]?.type, element[key])}</>,
    hideOnMobile: index >= 3,
  }));

  return (
    <>
      <ResponsiveDataTable
        elements={sortedElements}
        columns={tableColumns}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        verifiedIconRender={(element) => <VerifiedIcon element={element} />}
        rowProps={(element) => ({
          onClick: () => onOpenEntry?.(element.id, "view", { replace: false }),
          style: { cursor: "pointer" }
        })}
      />
      <Drawer
        opened={opened}
        onClose={() => {
          onCloseEntry?.({ replace: false });
        }}
        title="Data Entry"
        padding="lg"
        size={isMobile ? "100%" : "800px"}
        position="right"
      >
        {!!notification && (
          <Alert
            icon={
              notification?.success ? <Check size={18} /> : <InfoCircle size={18} />
            }
            color={notification?.success ? "green" : "red"}
            title="Data update"
          >
            {notification.message}
          </Alert>
        )}
        <Divider />
        <div style={{ overflow: "scroll", height: "100%" }}>
          {focus && (
            <div style={{ marginBottom: "8em" }}>
              <form onSubmit={form.onSubmit((values) => updateEntry(values, focus))}>
                <Stack
                  style={{
                    marginTop: "1em",
                    marginBottom: "10em",
                    overflow: "scroll",
                  }}
                >
                  {detailsSortedKeys.map((key) => (
                    <div key={key}>
                      {fieldConfig[key]?.type !== "IMAGE" ? (
                        // display non-image type
                        <>
                          <DataEntry
                            label={fieldConfig[key]?.label}
                            edit={doEdit && fieldConfig[key]?.editable}
                            inputProps={form.getInputProps(key)}
                            dataType={fieldConfig[key]?.type}
                            form={form}
                            fieldKey={key}
                            value={formatValue(
                              fieldConfig[key]?.type,
                              focus[key]
                            )}
                            selectOptions={fieldConfig[key]?.sourceCollection ? selectOptions[fieldConfig[key].sourceCollection] : []}
                            selectLabelKey={fieldConfig[key]?.sourceFieldForLabel}
                            adjacentFields={fieldConfig[key]?.adjacentFields}
                          />
                          {doEdit &&
                            key === "odoOrHourReading" &&
                            fieldConfig[key]?.editable &&
                            activeFleetId && (
                              <div style={{ marginTop: "8px" }}>
                                <Checkbox
                                  checked={syncFleetOdometer}
                                  onChange={(event) => {
                                    setUserToggledSyncOdo(true);
                                    setSyncFleetOdometer(event.currentTarget.checked);
                                  }}
                                  label={
                                    Number.isFinite(parsedFormOdo)
                                      ? `Also set vehicle (${activeVehicle?.name || "selected vehicle"}) current odometer to ${parsedFormOdo}`
                                      : `Also update vehicle (${activeVehicle?.name || "selected vehicle"}) current odometer to this value`
                                  }
                                />
                                {activeVehicle?.lastOdometer !== undefined &&
                                  activeVehicle?.lastOdometer !== null && (
                                    <Text size="xs" color="dimmed" style={{ marginTop: "4px", marginLeft: "28px" }}>
                                      Current vehicle odometer: {activeVehicle.lastOdometer}
                                    </Text>
                                  )}
                              </div>
                            )}
                        </>
                      ) : (
                        // display image type
                        <>
                          <Text weight={500}>{fieldConfig[key]?.label}</Text>
                          <div
                            style={{
                              width: 250,
                              marginLeft: "auto",
                              marginRight: "auto",
                              marginTop: "1em",
                            }}
                          >
                            <div style={{ display: "grid", rowGap: "1em" }}>
                              {!!focus[key]?.length
                                ? focus[key]?.map((imageMap: any, i: number) => (
                                  <Image key={i} src={imageMap?.path} />
                                ))
                                : "No image"}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </Stack>
                <div
                  style={{
                    position: "fixed",
                    bottom: 0,
                    width: "100%",
                    // negative margin to counteract mantine padding
                    margin: "0 -20px",
                    backgroundColor: "white",
                  }}
                >
                  {/* Larger screen size */}
                  <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
                    <div>
                      {loading ? (
                        <Loader
                          style={{ margin: "1em auto", width: "100%" }}
                          color="blue"
                        ></Loader>
                      ) : (
                        <div
                          style={{
                            display: "grid",
                            columnGap: "1em",
                            gridAutoColumns: "1fr",
                            gridAutoFlow: "column",
                            margin: "20px",
                          }}
                        >
                          {renderButtons("desktop")}
                        </div>
                      )}
                    </div>
                  </MediaQuery>
                  {/* Smaller screen size */}
                  <MediaQuery largerThan="sm" styles={{ display: "none" }}>
                    <div>
                      {loading ? (
                        <Loader
                          style={{ margin: "1em auto", width: "100%" }}
                          color="blue"
                        ></Loader>
                      ) : (
                        <div
                          style={{
                            display: "grid",
                            rowGap: "1em",
                            gridAutoRows: "1fr",
                            gridAutoFlow: "row",
                            margin: "20px",
                          }}
                        >
                          {renderButtons("mobile")}
                        </div>
                      )}
                    </div>
                  </MediaQuery>
                </div>
              </form>
            </div>
          )}
        </div>
      </Drawer>
      <DownloadAlert
        renderType={renderType}
        reportName={reportName}
        Base64File={Base64File}
        modalOpen={downloadModalOpen}
        onModalClose={() => setDownloadModalOpen(false)}
      ></DownloadAlert>
    </>
  );
}
